// hooks/useProductTabs.js
// Tab management for multi-product creation
// IMPROVED: Better status management, edit tracking, unsaved changes detection

import { useState, useCallback, useMemo, useRef } from "react";
import { nanoid } from "nanoid";

// ============= ALIGNED CONSTANTS FROM PRODUCTFORM =============
export const PRODUCT_TYPE_ENUM = [
  "GOLD",
  "SILVER",
  "PLATINUM",
  "DIAMOND",
  "STUDDED",
];
export const PRODUCT_STATUS_ENUM = ["ACTIVE", "INACTIVE", "DRAFT", "ARCHIVED"];
export const PRODUCT_GENDER_ENUM = ["MALE", "FEMALE", "UNISEX", "KIDS"];
export const VIEW_ACCESS_ENUM = [
  "B2C_APP",
  "B2C_WEBSITE",
  "B2B_WEBSITE",
  "B2B_APP",
];
export const PURITY_ENUM = ["24K", "22K", "18K", "14K", "925", "950", "PT950"];
export const METAL_COLOR_ENUM = [
  "YELLOW",
  "WHITE",
  "ROSE",
  "TWO_TONE",
  "THREE_TONE",
];
export const PRODUCT_SUBTYPE_ENUM = [
  "RING",
  "NECKLACE",
  "EARRING",
  "BRACELET",
  "PENDANT",
  "CHAIN",
  "BANGLE",
  "ANKLET",
  "NOSE_PIN",
  "MANGALSUTRA",
  "KADA",
  "COIN",
  "BAR",
  "IDOL",
  "OTHER",
];
export const CRAFT_TYPE_ENUM = [
  "HANDMADE",
  "MACHINE_MADE",
  "CAST",
  "HANDCRAFTED",
  "STAMPED",
];
export const TECHNIQUE_ENUM = [
  "KUNDAN",
  "MEENAKARI",
  "TEMPLE",
  "FILIGREE",
  "ANTIQUE",
  "MODERN",
  "TRADITIONAL",
];
export const STONE_TYPE_ENUM = [
  "DIAMOND",
  "RUBY",
  "EMERALD",
  "SAPPHIRE",
  "PEARL",
  "TOPAZ",
  "AMETHYST",
  "OTHER",
];
export const STONE_SHAPE_ENUM = [
  "ROUND",
  "PRINCESS",
  "OVAL",
  "MARQUISE",
  "PEAR",
  "CUSHION",
  "EMERALD",
  "ASSCHER",
  "RADIANT",
  "HEART",
];
export const STONE_SETTING_ENUM = [
  "PRONG",
  "BEZEL",
  "CHANNEL",
  "PAVE",
  "TENSION",
  "HALO",
  "CLUSTER",
];
export const CERTIFICATION_TYPE_ENUM = [
  "BIS_HALLMARK",
  "GIA",
  "IGI",
  "SGL",
  "HRD",
  "AGS",
  "DGLA",
];
export const CERTIFICATION_AGENCY_ENUM = [
  "BIS",
  "GIA",
  "IGI",
  "SGL",
  "HRD",
  "AGS",
  "DGLA",
];

// Tab status types
export const TAB_STATUS = {
  DRAFT: "draft",
  QUEUED: "queued",
  SAVING: "saving",
  SAVED: "saved",
  FAILED: "failed",
  EDITED: "edited", // Edited after save/fail
};

// ============= DEFAULT STONE (ALIGNED) =============
export const DEFAULT_STONE = {
  type: STONE_TYPE_ENUM[0],
  weight: 0,
  count: 1,
  color: "",
  clarity: "",
  cut: "",
  shape: STONE_SHAPE_ENUM[0],
  settingType: STONE_SETTING_ENUM[0],
  certification: {
    agency: "",
    certificateNumber: "",
    certificateUrl: "",
  },
  ratePerCarat: 0,
  totalValue: 0,
};

// ============= DEFAULT CERTIFICATION (ALIGNED) =============
export const DEFAULT_CERTIFICATION = {
  type: CERTIFICATION_TYPE_ENUM[0],
  number: "",
  issuedBy: "",
  issuedDate: new Date(),
  validUntil: null,
  documentUrl: "",
};

// ============= DEFAULT PRODUCT DATA (ALIGNED WITH PRODUCTFORM) =============
export const generateDefaultProduct = (primaryBranch) => ({
  // Basic fields
  productId: "",
  sku: "",
  name: "",
  description: "",
  type: PRODUCT_TYPE_ENUM[0],
  subType: "",
  categories: [],
  collections: [],

  // Weights
  grossWeight: "",
  netWeight: "",

  // Metal details
  purity: "",
  metalColor: METAL_COLOR_ENUM[0],
  metalDetails: {
    purity: "",
    color: METAL_COLOR_ENUM[0],
    fineness: null,
    karat: null,
  },

  // Stone/Diamond Information
  hasStones: false,
  stones: [],
  totalStoneWeight: 0,
  stoneCharges: 0,

  // Manufacturing
  karigarCode: "",
  craftType: "",
  technique: "",
  manufacturing: {
    karigarCode: "",
    craftType: "",
    technique: "",
    origin: "",
    productionDate: new Date(),
    batchNumber: "",
  },

  // Stock management
  stock: {
    totalQuantity: 1,
    reservedQuantity: 0,
    availableQuantity: 1,
    minStockLevel: 1,
    maxStockLevel: null,
    locationStock: [],
  },

  // Branch management
  branchInfo: {
    primaryBranch: primaryBranch || "",
    availableBranches: primaryBranch ? [primaryBranch] : [],
    availableStores: [],
  },

  // Status & Display
  status: PRODUCT_STATUS_ENUM[2], // DRAFT
  viewAccess: ["B2C_WEBSITE", "B2C_APP"],
  gender: "UNISEX",
  occasion: [],
  tags: [],
  isFeatured: false,
  isNewArrival: false,
  isBestseller: false,
  isExclusive: false,
  isPrivate: false,

  // Hallmark
  hallmark: {
    isHallmarked: false,
    huid: "",
    centerCode: "",
    year: new Date().getFullYear().toString(),
    jewellerCode: "",
    purity: "",
  },

  // Certifications
  certifications: [],

  // Size/Variant Information
  sizeInfo: {
    hasSize: false,
    availableSizes: [],
    defaultSize: "",
    sizeChart: "",
  },
  isVariant: false,
  parentProduct: null,
  variants: [],
  variantAttribute: "",

  // Customization Options
  customizable: {
    isCustomizable: false,
    options: [],
  },

  // Pricing config
  pricingConfig: {
    useCustomPricing: false,
    customMakingCharges: null,
    customWastagePercent: null,
    excludeFromOffers: false,
    branchSpecificRules: [],
  },

  // Media
  media: [],

  // SEO reference
  productSeo: null,

  // Legacy fields
  narration: "",
  isStone: false,
  stoneWeight: 0,
});

// Check if product has meaningful changes (for unsaved changes detection)
const hasProductData = (data) => {
  if (!data) return false;
  return !!(
    data.name?.trim() ||
    parseFloat(data.grossWeight) > 0 ||
    data.type !== "GOLD" ||
    data.subType ||
    data.karigarCode?.trim()
  );
};

// Deep compare two objects (simplified for form data)
const hasDataChanged = (original, current) => {
  if (!original || !current) return true;

  // Compare key fields that indicate edits
  const keyFields = [
    "name",
    "type",
    "subType",
    "purity",
    "metalColor",
    "grossWeight",
    "netWeight",
    "karigarCode",
    "craftType",
    "technique",
    "gender",
    "status",
    "description",
    "hasStones",
  ];

  for (const field of keyFields) {
    if (String(original[field] || "") !== String(current[field] || "")) {
      return true;
    }
  }

  // Compare arrays
  if (
    JSON.stringify(original.categories) !== JSON.stringify(current.categories)
  ) {
    return true;
  }
  if (JSON.stringify(original.stones) !== JSON.stringify(current.stones)) {
    return true;
  }
  if (
    JSON.stringify(original.stock?.locationStock) !==
    JSON.stringify(current.stock?.locationStock)
  ) {
    return true;
  }

  return false;
};

export const useProductTabs = (primaryBranch) => {
  const [tabs, setTabs] = useState(() => {
    const initialTab = {
      id: nanoid(8),
      data: generateDefaultProduct(primaryBranch),
      originalData: null, // Store original data after save/fail for comparison
      status: TAB_STATUS.DRAFT,
      createdAt: Date.now(),
      lastModified: Date.now(),
      savedProductId: null, // Store the saved product ID after successful save
      lastError: null, // Store last error message
    };
    return [initialTab];
  });

  const [activeTabId, setActiveTabId] = useState(() => tabs[0]?.id);

  // Track if there are unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return tabs.some(
      (tab) => tab.status === TAB_STATUS.DRAFT && hasProductData(tab.data)
    );
  }, [tabs]);

  // Add new tab
  const addTab = useCallback(
    (initialData = null) => {
      const newTab = {
        id: nanoid(8),
        data: initialData || generateDefaultProduct(primaryBranch),
        originalData: null,
        status: TAB_STATUS.DRAFT,
        createdAt: Date.now(),
        lastModified: Date.now(),
        savedProductId: null,
        lastError: null,
      };

      setTabs((prev) => [...prev, newTab]);
      setActiveTabId(newTab.id);

      return newTab.id;
    },
    [primaryBranch]
  );

  // Remove tab with unsaved changes check
  const removeTab = useCallback(
    (tabId, force = false) => {
      const tab = tabs.find((t) => t.id === tabId);

      // Return info about the tab for confirmation
      if (
        !force &&
        tab &&
        tab.status === TAB_STATUS.DRAFT &&
        hasProductData(tab.data)
      ) {
        return {
          needsConfirmation: true,
          tabName:
            tab.data.name ||
            `Product ${tabs.findIndex((t) => t.id === tabId) + 1}`,
        };
      }

      setTabs((prev) => {
        if (prev.length <= 1) return prev;

        const newTabs = prev.filter((t) => t.id !== tabId);

        if (tabId === activeTabId) {
          const removedIndex = prev.findIndex((t) => t.id === tabId);
          const newActiveIndex = Math.min(removedIndex, newTabs.length - 1);
          setActiveTabId(newTabs[newActiveIndex]?.id);
        }

        return newTabs;
      });

      return { needsConfirmation: false };
    },
    [activeTabId, tabs]
  );

  // Force remove tab (after confirmation)
  const forceRemoveTab = useCallback(
    (tabId) => {
      setTabs((prev) => {
        if (prev.length <= 1) return prev;

        const newTabs = prev.filter((t) => t.id !== tabId);

        if (tabId === activeTabId) {
          const removedIndex = prev.findIndex((t) => t.id === tabId);
          const newActiveIndex = Math.min(removedIndex, newTabs.length - 1);
          setActiveTabId(newTabs[newActiveIndex]?.id);
        }

        return newTabs;
      });
    },
    [activeTabId]
  );

  // Duplicate tab
  const duplicateTab = useCallback(
    (tabId) => {
      const sourceTab = tabs.find((t) => t.id === tabId);
      if (!sourceTab) return null;

      const newTab = {
        id: nanoid(8),
        data: {
          ...JSON.parse(JSON.stringify(sourceTab.data)),
          name: `${sourceTab.data.name || "Product"} (Copy)`,
          productId: "",
          sku: "",
        },
        originalData: null,
        status: TAB_STATUS.DRAFT,
        createdAt: Date.now(),
        lastModified: Date.now(),
        savedProductId: null,
        lastError: null,
      };

      setTabs((prev) => {
        const sourceIndex = prev.findIndex((t) => t.id === tabId);
        const newTabs = [...prev];
        newTabs.splice(sourceIndex + 1, 0, newTab);
        return newTabs;
      });

      setActiveTabId(newTab.id);
      return newTab.id;
    },
    [tabs]
  );

  // Bulk duplicate
  const bulkDuplicate = useCallback(
    (tabId, count) => {
      const sourceTab = tabs.find((t) => t.id === tabId);
      if (!sourceTab) return [];

      const newTabs = Array.from({ length: count }, (_, i) => ({
        id: nanoid(8),
        data: {
          ...JSON.parse(JSON.stringify(sourceTab.data)),
          name: `${sourceTab.data.name || "Product"} (${i + 1})`,
          productId: "",
          sku: "",
        },
        originalData: null,
        status: TAB_STATUS.DRAFT,
        createdAt: Date.now(),
        lastModified: Date.now(),
        savedProductId: null,
        lastError: null,
      }));

      setTabs((prev) => {
        const sourceIndex = prev.findIndex((t) => t.id === tabId);
        const newTabsList = [...prev];
        newTabsList.splice(sourceIndex + 1, 0, ...newTabs);
        return newTabsList;
      });

      return newTabs.map((t) => t.id);
    },
    [tabs]
  );

  // Update tab data - IMPROVED: Reset status to draft if edited after save/fail
  const updateTabData = useCallback((tabId, newData) => {
    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== tabId) return tab;

        // Determine new status based on current status and if data changed
        let newStatus = tab.status;

        // If tab was saved/failed/edited and data changed, reset to draft for re-save
        if (
          tab.status === TAB_STATUS.SAVED ||
          tab.status === TAB_STATUS.FAILED ||
          tab.status === TAB_STATUS.EDITED
        ) {
          // Check if data actually changed from original
          if (tab.originalData && hasDataChanged(tab.originalData, newData)) {
            newStatus = TAB_STATUS.DRAFT;
          }
        }

        // If tab was queued/saving, mark as edited (will need re-save)
        if (
          tab.status === TAB_STATUS.QUEUED ||
          tab.status === TAB_STATUS.SAVING
        ) {
          if (hasDataChanged(tab.data, newData)) {
            newStatus = TAB_STATUS.EDITED;
          }
        }

        return {
          ...tab,
          data: newData,
          status: newStatus,
          lastModified: Date.now(),
        };
      })
    );
  }, []);

  // Update tab status - IMPROVED: Store original data on status changes
  const updateTabStatus = useCallback(
    (tabId, status, error = null, savedProductId = null) => {
      setTabs((prev) =>
        prev.map((tab) => {
          if (tab.id !== tabId) return tab;

          const updates = {
            status,
            lastModified: Date.now(),
            lastError: error,
          };

          // Store original data when queued or saved/failed
          if (status === TAB_STATUS.QUEUED) {
            updates.originalData = JSON.parse(JSON.stringify(tab.data));
          }

          // Store saved product ID on success
          if (status === TAB_STATUS.SAVED && savedProductId) {
            updates.savedProductId = savedProductId;
          }

          return { ...tab, ...updates };
        })
      );
    },
    []
  );

  // Mark tab as ready for re-save (after editing a failed/saved product)
  const markForReSave = useCallback((tabId) => {
    setTabs((prev) =>
      prev.map((tab) =>
        tab.id === tabId
          ? {
              ...tab,
              status: TAB_STATUS.DRAFT,
              lastModified: Date.now(),
              originalData: null, // Clear original data
              lastError: null,
            }
          : tab
      )
    );
  }, []);

  // Get active tab
  const getActiveTab = useCallback(() => {
    return tabs.find((t) => t.id === activeTabId) || tabs[0];
  }, [tabs, activeTabId]);

  // Set active tab
  const setActiveTab = useCallback(
    (tabId) => {
      if (tabs.some((t) => t.id === tabId)) {
        setActiveTabId(tabId);
      }
    },
    [tabs]
  );

  // Get tabs that can be saved (draft status with valid data)
  const getSaveableTabs = useCallback(() => {
    return tabs.filter((tab) => {
      const grossWeight = parseFloat(tab.data.grossWeight);
      return (
        (tab.status === TAB_STATUS.DRAFT || tab.status === TAB_STATUS.EDITED) &&
        tab.data.name?.trim() &&
        !isNaN(grossWeight) &&
        grossWeight > 0
      );
    });
  }, [tabs]);

  // Get count of tabs needing attention (failed, edited)
  const getAttentionCount = useCallback(() => {
    return tabs.filter(
      (tab) =>
        tab.status === TAB_STATUS.FAILED || tab.status === TAB_STATUS.EDITED
    ).length;
  }, [tabs]);

  // Reset tab to draft (for starting over)
  const resetTab = useCallback(
    (tabId) => {
      setTabs((prev) =>
        prev.map((tab) =>
          tab.id === tabId
            ? {
                ...tab,
                data: generateDefaultProduct(primaryBranch),
                originalData: null,
                status: TAB_STATUS.DRAFT,
                lastModified: Date.now(),
                savedProductId: null,
                lastError: null,
              }
            : tab
        )
      );
    },
    [primaryBranch]
  );

  return {
    tabs,
    activeTabId,
    addTab,
    removeTab,
    forceRemoveTab,
    duplicateTab,
    bulkDuplicate,
    updateTabData,
    updateTabStatus,
    markForReSave,
    getActiveTab,
    setActiveTab,
    getSaveableTabs,
    getAttentionCount,
    resetTab,
    hasUnsavedChanges,
    TAB_STATUS,
  };
};
